import React, { PureComponent } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import {
  defaultOptions,
  checkStatus,
  parseJSON,
  getLocationOrigin,
} from "../../../services/fetchTools";
import {
  colors,
  shadows,
  card,
  btn,
  breadcrumb,
  empty,
} from "../../../style/common/partnerStyle";

class AnnouncementDetails extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      announcement: null,
      error: null,
    };
  }

  componentDidMount() {
    const stateData = this.props.location && this.props.location.state;
    if (stateData && stateData.announcement) {
      this.setState({ announcement: stateData.announcement, loading: false });
    } else {
      this.fetchAnnouncement();
    }
  }

  fetchAnnouncement() {
    const { id } = this.props.match.params;
    const url = `${getLocationOrigin()}/notification/get_announcement/?id=${id}`;
    const options = { ...defaultOptions };
    fetch(url, options)
      .then(checkStatus)
      .then(parseJSON)
      .then((data) => this.setState({ announcement: data.data, loading: false }))
      .catch(() => this.setState({ loading: false, error: "Failed to load announcement." }));
  }

  goBack = () => this.props.history.goBack();

  renderSkeleton() {
    const skel = (w, h, r = 6) => ({
      width: w, height: h, borderRadius: r,
      background: "linear-gradient(90deg,#f0f2f5 25%,#e4e8ed 50%,#f0f2f5 75%)",
      backgroundSize: "400% 100%",
      animation: "skshimmer 1.4s ease infinite",
    });
    return (
      <div style={S.page}>
        <style>{`@keyframes skshimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}`}</style>
        <div style={breadcrumb.wrap}>
          <div style={skel(90, 13)} /><span style={{ color: colors.border }}>›</span><div style={skel(160, 13)} />
        </div>
        <div style={S.body}>
          <div style={S.main}>
            <div style={card.wrap}>
              <div style={skel("100%", 220, "12px 12px 0 0")} />
              <div style={{ padding: "22px 28px 24px" }}>
                <div style={{ ...skel("55%", 22), marginBottom: 12 }} />
                <div style={{ ...skel("32%", 13), marginBottom: 20 }} />
                <div style={S.statStrip}>
                  {[1,2,3,4].map(i => <div key={i} style={{ ...skel("100%", 72), borderRadius: 10 }} />)}
                </div>
              </div>
            </div>
            <div style={card.wrap}>
              <div style={{ ...card.header, borderBottom: `1px solid ${colors.border}` }}>
                <div style={skel(60, 14)} />
              </div>
              <div style={{ padding: "20px 28px", display: "flex", flexDirection: "column", gap: 10 }}>
                {[100, 90, 95, 80, 70, 85].map((w, i) => (
                  <div key={i} style={skel(w + "%", 13)} />
                ))}
              </div>
            </div>
          </div>
          <div style={S.sidebar}>
            {[1,2,3].map(i => (
              <div key={i} style={card.wrap}>
                <div style={{ ...card.header }}>
                  <div style={skel(70, 13)} />
                </div>
                <div style={{ padding: "14px 20px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {[70, 90, 60, 80].map((w, j) => <div key={j} style={skel(w, 26, 99)} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, announcement, error } = this.state;
    if (loading) return this.renderSkeleton();

    if (error || !announcement) {
      return (
        <div style={S.page}>
          <div style={{ ...card.wrap, margin: "40px auto", maxWidth: 480, padding: 48, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
            <div style={empty.title}>{error || "Announcement not found"}</div>
            <div style={{ ...empty.text, marginBottom: 24 }}>
              It may have been deleted or you may not have access.
            </div>
            <button style={btn.outline} onClick={this.goBack}>← Back to list</button>
          </div>
        </div>
      );
    }

    const {
      title, created_on, created_by, body,
      image, support_document, recepients, cities, venues,
    } = announcement;

    const total    = recepients.total_count || 0;
    const read     = recepients.read_count || 0;
    const active   = recepients.active_after_notification || 0;
    const installs = recepients.app_installed || 0;

    const readPct   = total > 0 ? Math.round((read   / total) * 100) : 0;
    const activePct = total > 0 ? Math.round((active / total) * 100) : 0;

    const readColor =
      readPct >= 60 ? colors.success :
      readPct >= 30 ? colors.amber   : colors.danger;

    const stats = [
      { label: "Total Sent",   value: total.toLocaleString(),    icon: "📤", color: colors.brand,   bg: colors.brandLt   },
      { label: "App Installs", value: installs.toLocaleString(), icon: "📱", color: "#7c3aed",      bg: "#ede9fe"        },
      { label: "Read",         value: read.toLocaleString(),     icon: "👁",  color: readColor,      bg: readColor + "18" },
      { label: "Active After", value: active.toLocaleString(),   icon: "⚡", color: colors.success, bg: colors.successLt },
    ];

    return (
      <div style={S.page}>
        <style>{`
          .ann-body-content p        { margin: 0 0 14px; }
          .ann-body-content ul, .ann-body-content ol { padding-left: 22px; margin: 0 0 14px; }
          .ann-body-content li       { margin-bottom: 6px; }
          .ann-body-content strong   { color: ${colors.text}; }
          .ann-stat-card             { transition: transform 0.15s, box-shadow 0.15s; }
          .ann-stat-card:hover       { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0,0,0,0.09); }
          .ann-venue-tag:hover       { background: ${colors.brandLt} !important; color: ${colors.brand} !important; border-color: #c3d9fc !important; }
        `}</style>

        {/* ── Breadcrumb ── */}
        <div style={breadcrumb.wrap}>
          <span style={{ ...breadcrumb.link, cursor: "pointer" }} onClick={this.goBack}>
            Announcements
          </span>
          <span style={breadcrumb.separator}>›</span>
          <span style={{
            ...breadcrumb.current,
            maxWidth: 420,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
            verticalAlign: "middle",
          }}>
            {title}
          </span>
        </div>

        <div style={S.body}>

          {/* ════ MAIN COLUMN ════ */}
          <div style={S.main}>

            {/* ── Hero card ── */}
            <div style={card.wrap}>
              {image && (
                <div style={{ borderRadius: "12px 12px 0 0", overflow: "hidden", maxHeight: 280 }}>
                  <img
                    src={image}
                    alt={title}
                    style={{ width: "100%", display: "block", maxHeight: 280, objectFit: "cover" }}
                    onError={(e) => { e.target.closest("div").style.display = "none"; }}
                  />
                </div>
              )}
              <div style={{ padding: "22px 28px 26px" }}>
                <h1 style={S.title}>{title}</h1>
                <div style={S.metaRow}>
                  <span style={S.metaChip}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginRight: 5, flexShrink: 0 }}>
                      <rect x="1" y="1.5" width="10" height="9.5" rx="1.5" stroke={colors.muted} strokeWidth="1.2"/>
                      <path d="M1 5h10M4 1v2M8 1v2" stroke={colors.muted} strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    {moment(created_on).format("DD MMM YYYY, h:mm A")}
                  </span>
                  <span style={S.metaDot} />
                  <span style={S.metaChip}>
                    <div style={S.avatarXs}>
                      {created_by ? created_by[0].toUpperCase() : "?"}
                    </div>
                    {created_by}
                  </span>
                </div>

                {/* Stat strip */}
                <div style={S.statStrip}>
                  {stats.map(({ label, value, icon, color, bg }) => (
                    <div key={label} className="ann-stat-card" style={{ ...S.statCard, background: bg }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{icon}</span>
                      <span style={{ ...S.statValue, color }}>{value}</span>
                      <span style={S.statLabel}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Message body ── */}
            <div style={card.wrap}>
              <div style={card.header}>
                <h2 style={card.title}>Message</h2>
              </div>
              <div style={{ padding: "20px 28px" }}>
                <div
                  className="ann-body-content"
                  style={{ fontSize: 15, lineHeight: 1.75, color: colors.text }}
                  dangerouslySetInnerHTML={{ __html: body || "<p>No content</p>" }}
                />
              </div>
            </div>

            {/* ── Attachment ── */}
            {support_document && (
              <div style={card.wrap}>
                <div style={card.header}>
                  <h2 style={card.title}>Attachment</h2>
                </div>
                <div style={{ padding: "16px 28px" }}>
                  <a
                    href={support_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "9px 16px",
                      background: colors.brandLt,
                      color: colors.brand,
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ marginRight: 7 }}>
                      <path d="M4 1h4l3 3v9H3V1h1z" stroke="currentColor" strokeWidth="1.3" fill="none"/>
                      <path d="M8 1v3h3" stroke="currentColor" strokeWidth="1.3"/>
                    </svg>
                    View Document
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* ════ SIDEBAR ════ */}
          <div style={S.sidebar}>

            {/* ── Engagement ── */}
            <div style={card.wrap}>
              <div style={card.header}>
                <h2 style={card.title}>Engagement</h2>
              </div>
              <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 20 }}>
                <RateBar
                  label="Read Rate"
                  pct={readPct}
                  color={readColor}
                  sub={`${read.toLocaleString()} of ${total.toLocaleString()} recipients`}
                />
                <div style={{ borderTop: `1px solid ${colors.border}` }} />
                <RateBar
                  label="Active After"
                  pct={activePct}
                  color={colors.brand}
                  sub={`${active.toLocaleString()} of ${total.toLocaleString()} recipients`}
                />
              </div>
            </div>

            {/* ── Cities ── */}
            {cities && cities.length > 0 && (
              <div style={card.wrap}>
                <div style={card.header}>
                  <h2 style={card.title}>Cities</h2>
                  <span style={S.countBadge}>{cities.length}</span>
                </div>
                <div style={{ padding: "14px 20px" }}>
                  <div style={S.tagCloud}>
                    {cities.map((c) => (
                      <span key={c.id} style={S.cityTag}>{c.name}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Venues ── */}
            {venues && venues.length > 0 && (
              <div style={card.wrap}>
                <div style={card.header}>
                  <h2 style={card.title}>Venues</h2>
                  <span style={S.countBadge}>{venues.length}</span>
                </div>
                <div style={{ padding: "14px 20px" }}>
                  <div style={S.tagCloud}>
                    {venues.map((v) => (
                      <span
                        key={v.id}
                        className="ann-venue-tag"
                        style={{ ...S.venueTag, transition: "background 0.15s, color 0.15s, border-color 0.15s" }}
                      >
                        {v.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }
}

/* ── Rate bar sub-component ── */
function RateBar({ label, pct, color, sub }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{label}</span>
        <span style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{pct}%</span>
      </div>
      <div style={{ height: 8, background: colors.border, borderRadius: 99, overflow: "hidden" }}>
        <div style={{
          height: "100%",
          width: pct + "%",
          background: color,
          borderRadius: 99,
          transition: "width 0.5s ease",
        }} />
      </div>
      <div style={{ fontSize: 11, color: colors.muted, marginTop: 6 }}>{sub}</div>
    </div>
  );
}

/* ── Local styles (only what sharedStyles doesn't cover) ── */
const S = {
  page: {
    fontFamily: "'DM Sans','Segoe UI',sans-serif",
    background: colors.bg,
    minHeight: "100vh",
    boxSizing: "border-box",
  },
  body: {
    display: "flex",
    gap: 20,
    alignItems: "flex-start",
    padding: "4px 20px 32px",
  },
  main: {
    flex: "1 1 0",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  sidebar: {
    width: 296,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  title: {
    fontSize: 21,
    fontWeight: 700,
    color: colors.text,
    margin: "0 0 10px",
    lineHeight: 1.35,
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 22,
    flexWrap: "wrap",
  },
  metaChip: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    color: colors.muted,
    fontWeight: 500,
  },
  metaDot: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: colors.border,
    display: "inline-block",
    flexShrink: 0,
  },
  avatarXs: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: colors.brandLt,
    color: colors.brand,
    fontSize: 10,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 6,
    flexShrink: 0,
  },
  statStrip: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 10,
  },
  statCard: {
    borderRadius: 10,
    padding: "14px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  statValue: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: colors.muted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  countBadge: {
    background: colors.border,
    color: colors.muted,
    borderRadius: 99,
    padding: "2px 9px",
    fontSize: 11,
    fontWeight: 700,
  },
  tagCloud: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  cityTag: {
    background: colors.brandLt,
    color: colors.brand,
    border: `1px solid #c3d9fc`,
    borderRadius: 99,
    padding: "4px 11px",
    fontSize: 12,
    fontWeight: 600,
  },
  venueTag: {
    background: colors.bg,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: 99,
    padding: "4px 11px",
    fontSize: 12,
    cursor: "default",
  },
};

export default withRouter(AnnouncementDetails);


// WEBPACK FOOTER //
// ./src/app/views/partner/announcements/announcementDetails.js